/**
 * Curve fitting for (x, y) data: polynomial, exponential, logarithmic and power models,
 * all fitted by least squares and scored on the same footing so they can be compared.
 *
 * ## How each model is fitted
 *
 * - **Polynomial** of degree d (d = 1 is a straight line): linear least squares by QR on
 *   the powers of u = (x − c) / s with c the mean and s the half-range of x, so a degree-6
 *   fit to the years 1990–2020 is as accurate as one to 0–1. The coefficients are then
 *   expanded back to powers of x; predictions and plots use the centred form, which does
 *   not suffer the cancellation the expanded coefficients can.
 * - **Logarithmic** y = a + b ln x: linear least squares on ln x (needs x > 0).
 * - **Exponential** y = a·e^{bx} and **power** y = a·x^b: either *transformed* (straight
 *   line through ln y, the way spreadsheet trendlines do it, so the fit is best on the log
 *   scale and can be poor on the original) or fitted *on the original scale* by
 *   Levenberg–Marquardt non-linear least squares started from the transformed fit, which
 *   minimises the squared errors in y itself (what R's `nls` does). Both are in the
 *   tests.
 *
 * ## Comparing models
 *
 * Every model's R² is 1 − SSE/SST on the **original** y, so a transformed fit is judged
 * by how well it predicts y, not ln y (its R² on the log scale is also reported — that is
 * the number a spreadsheet trendline shows). AIC, BIC and AICc use R's definitions
 * (`AIC(lm)` / `AIC(nls)`: −2 log L + 2(p + 1), counting the error variance), with
 * log L = −n/2 (ln 2π + 1 − ln n + ln SSE), so they match R when the same model is fitted.
 * They are only comparable between fits to the same y — a transformed fit is compared on
 * its original-scale SSE.
 */

import { mean } from "@/lib/stats/descriptive";
import { studentTTwoTailed } from "@/lib/stats/distributions";
import { qrLeastSquares } from "@/lib/tool-kit/stats-regression/linalg";

export type ModelId =
  | "linear"
  | "poly2"
  | "poly3"
  | "poly4"
  | "poly5"
  | "poly6"
  | "exponential"
  | "logarithmic"
  | "power";

export const MODEL_IDS: readonly ModelId[] = [
  "linear",
  "poly2",
  "poly3",
  "poly4",
  "poly5",
  "poly6",
  "exponential",
  "logarithmic",
  "power",
];

export type FitMethod = "original" | "transformed";

export interface Parameter {
  /** "a", "b", … or "x^2" for polynomial terms (the widget words these). */
  name: string;
  value: number;
  se: number;
  t: number;
  p: number;
}

export interface CurveFit {
  id: ModelId;
  /** Parameters in the order of the model's equation (a, b, … or intercept, x, x², …). */
  parameters: Parameter[];
  /** Parameters estimated, for AIC (the error variance is added on top). */
  k: number;
  predict: (x: number) => number;
  sse: number;
  sst: number;
  r2: number;
  adjR2: number;
  /** √(SSE / (n − k)). */
  rmse: number;
  aic: number;
  bic: number;
  aicc: number;
  /** R² of the straight-line fit on the transformed scale, for transformed models. */
  transformedR2: number | null;
  fitted: number[];
  residuals: number[];
  /** True when the optimiser reached its tolerance (always true for linear fits). */
  converged: boolean;
  /** Condition note: the expanded polynomial coefficients lose digits. */
  illConditioned: boolean;
  /** The centred polynomial form: c, s and coefficients of u = (x − c)/s. */
  centred: { centre: number; scale: number; coefficients: number[] } | null;
}

export type Unavailable =
  | "needsPositiveX"
  | "needsPositiveY"
  | "needsMoreData"
  | "needsDistinctX"
  | "failed";

export interface ModelResult {
  id: ModelId;
  fit: CurveFit | null;
  reason: Unavailable | null;
}

const POLY_DEGREE: Partial<Record<ModelId, number>> = {
  linear: 1,
  poly2: 2,
  poly3: 3,
  poly4: 4,
  poly5: 5,
  poly6: 6,
};

export function polynomialDegree(id: ModelId): number | null {
  return POLY_DEGREE[id] ?? null;
}

function binomial(n: number, k: number): number {
  let result = 1;
  for (let i = 1; i <= k; i++) {
    result = (result * (n - k + i)) / i;
  }
  return result;
}

/** Score a finished fit (R², information criteria) on the original y. */
function finish(
  id: ModelId,
  xs: readonly number[],
  ys: readonly number[],
  predict: (x: number) => number,
  parameters: Parameter[],
  extras: Partial<CurveFit> = {}
): CurveFit {
  const n = ys.length;
  const k = parameters.length;
  const fitted = xs.map(predict);
  const residuals = ys.map((y, i) => y - fitted[i]);
  const ybar = mean(ys);
  const sse = residuals.reduce((a, r) => a + r * r, 0);
  const sst = ys.reduce((a, y) => a + (y - ybar) ** 2, 0);
  const r2 = sst > 0 ? 1 - sse / sst : Number.NaN;
  const logLik =
    -(n / 2) * (Math.log(2 * Math.PI) + 1 - Math.log(n) + Math.log(sse));
  const aic = -2 * logLik + 2 * (k + 1);
  return {
    id,
    parameters,
    k,
    predict,
    sse,
    sst,
    r2,
    adjR2: 1 - ((1 - r2) * (n - 1)) / (n - k),
    rmse: Math.sqrt(sse / (n - k)),
    aic,
    bic: -2 * logLik + Math.log(n) * (k + 1),
    aicc: aic + (2 * (k + 1) * (k + 2)) / (n - (k + 1) - 1),
    transformedR2: null,
    fitted,
    residuals,
    converged: true,
    illConditioned: false,
    centred: null,
    ...extras,
  };
}

function parameter(
  name: string,
  value: number,
  se: number,
  df: number
): Parameter {
  const t = value / se;
  return {
    name,
    value,
    se,
    t,
    p: Number.isFinite(t) ? studentTTwoTailed(t, df) : Number.NaN,
  };
}

// ---------------------------------------------------------------------------
// Polynomials
// ---------------------------------------------------------------------------

function fitPolynomial(
  id: ModelId,
  degree: number,
  xs: readonly number[],
  ys: readonly number[]
): CurveFit | null {
  const n = xs.length;
  const centre = mean(xs);
  const half = (Math.max(...xs) - Math.min(...xs)) / 2;
  const scale = half > 0 ? half : 1;
  const us = xs.map((x) => (x - centre) / scale);
  const columns = Array.from({ length: degree + 1 }, (_, j) =>
    us.map((u) => u ** j)
  );
  const qr = qrLeastSquares(columns, ys);
  if (qr.rank < degree + 1) {
    return null;
  }
  const dfResidual = n - degree - 1;
  const s2 = qr.sse / dfResidual;
  const b = qr.coefficients;
  // Expand b₀ + b₁u + … into powers of x: aₖ = Σⱼ≥ₖ bⱼ s⁻ʲ C(j,k) (−c)^(j−k).
  const transform = Array.from({ length: degree + 1 }, (_, j) =>
    Array.from({ length: degree + 1 }, (_, k) =>
      k <= j ? (binomial(j, k) * (-centre) ** (j - k)) / scale ** j : 0
    )
  );
  const raw = Array.from({ length: degree + 1 }, (_, k) =>
    b.reduce((sum, bj, j) => sum + bj * transform[j][k], 0)
  );
  // Cov(a) = Tᵀ Cov(b) T, with Cov(b) = s² (UᵀU)⁻¹.
  const rawVariance = Array.from({ length: degree + 1 }, (_, k) => {
    let total = 0;
    for (let i = 0; i <= degree; i++) {
      for (let j = 0; j <= degree; j++) {
        total += transform[i][k] * qr.covariance[i][j] * transform[j][k];
      }
    }
    return s2 * total;
  });
  const names = Array.from({ length: degree + 1 }, (_, k) =>
    k === 0 ? "1" : `x${k}`
  );
  const parameters = raw.map((value, k) =>
    parameter(
      names[k],
      value,
      Math.sqrt(Math.max(0, rawVariance[k])),
      dfResidual
    )
  );
  const predict = (x: number) => {
    const u = (x - centre) / scale;
    let value = 0;
    for (let j = degree; j >= 0; j--) {
      value = value * u + b[j];
    }
    return value;
  };
  // Does evaluating the expanded coefficients reproduce the stable predictions?
  const sample = xs[Math.floor(n / 2)];
  const expanded = raw.reduceRight((acc, a) => acc * sample + a, 0);
  const reference = predict(sample);
  const illConditioned =
    Math.abs(expanded - reference) > 1e-6 * Math.max(1, Math.abs(reference));
  return finish(id, xs, ys, predict, parameters, {
    centred: { centre, scale, coefficients: b },
    illConditioned,
  });
}

// ---------------------------------------------------------------------------
// Logarithmic
// ---------------------------------------------------------------------------

function fitLogarithmic(
  xs: readonly number[],
  ys: readonly number[]
): CurveFit | null {
  const logs = xs.map(Math.log);
  const qr = qrLeastSquares([new Array<number>(xs.length).fill(1), logs], ys);
  if (qr.rank < 2) {
    return null;
  }
  const df = xs.length - 2;
  const s2 = qr.sse / df;
  const [a, b] = qr.coefficients;
  return finish("logarithmic", xs, ys, (x) => a + b * Math.log(x), [
    parameter("a", a, Math.sqrt(s2 * qr.covariance[0][0]), df),
    parameter("b", b, Math.sqrt(s2 * qr.covariance[1][1]), df),
  ]);
}

// ---------------------------------------------------------------------------
// Exponential and power: transformed and non-linear
// ---------------------------------------------------------------------------

/**
 * A two-parameter model f(t; A, b) in a well-scaled variable t, with analytic Jacobian.
 * Exponential: A·e^{b·t}, t = x − x̄.  Power: A·t^b, t = x / g (g the geometric mean).
 */
interface Scaled {
  /** The well-scaled variable for each original x. */
  toT: (x: number) => number;
  t: number[];
  value: (t: number, A: number, b: number) => number;
  jacobian: (t: number, A: number, b: number) => [number, number];
  /** Original a from the scaled A and b. */
  toA: (A: number, b: number) => number;
  /** d a / d A and d a / d b, for the delta method. */
  dA: (A: number, b: number) => [number, number];
}

function scaledModel(
  id: "exponential" | "power",
  xs: readonly number[]
): Scaled {
  if (id === "exponential") {
    const centre = mean(xs);
    const toT = (x: number) => x - centre;
    return {
      toT,
      t: xs.map(toT),
      value: (t, A, b) => A * Math.exp(b * t),
      jacobian: (t, A, b) => {
        const e = Math.exp(b * t);
        return [e, A * t * e];
      },
      toA: (A, b) => A * Math.exp(-b * centre),
      dA: (A, b) => {
        const f = Math.exp(-b * centre);
        return [f, -centre * A * f];
      },
    };
  }
  const g = Math.exp(mean(xs.map(Math.log)));
  const toT = (x: number) => x / g;
  return {
    toT,
    t: xs.map(toT),
    value: (t, A, b) => A * t ** b,
    jacobian: (t, A, b) => {
      const p = t ** b;
      return [p, A * p * Math.log(t)];
    },
    toA: (A, b) => A * g ** -b,
    dA: (A, b) => {
      const f = g ** -b;
      return [f, -Math.log(g) * A * f];
    },
  };
}

function sumSquares(values: readonly number[]): number {
  return values.reduce((a, v) => a + v * v, 0);
}

interface LmResult {
  A: number;
  b: number;
  sse: number;
  converged: boolean;
  /** (JᵀJ)⁻¹ at the solution. */
  covariance: number[][];
}

/** One damped Gauss–Newton step, solved by QR on the stacked system [J; √λ·D]. */
function dampedStep(
  jac: readonly [number, number][],
  residuals: readonly number[],
  lambda: number
): number[] | null {
  let d0 = 0;
  let d1 = 0;
  for (const [da, db] of jac) {
    d0 += da * da;
    d1 += db * db;
  }
  const step = qrLeastSquares(
    [
      [...jac.map((row) => row[0]), Math.sqrt(lambda * d0), 0],
      [...jac.map((row) => row[1]), 0, Math.sqrt(lambda * d1)],
    ],
    [...residuals, 0, 0]
  ).coefficients;
  return step.every((v) => Number.isFinite(v)) ? step : null;
}

/** Levenberg–Marquardt for y ≈ f(t; A, b). */
function levenbergMarquardt(
  model: Scaled,
  ys: readonly number[],
  start: [number, number]
): LmResult | null {
  let [A, b] = start;
  const residualsAt = (a: number, c: number) =>
    ys.map((y, i) => y - model.value(model.t[i], a, c));
  let residuals = residualsAt(A, b);
  let sse = sumSquares(residuals);
  let lambda = 1e-3;
  let converged = false;
  for (let iteration = 0; iteration < 500 && !converged; iteration++) {
    const jac = model.t.map((t) => model.jacobian(t, A, b));
    let improved = false;
    for (let attempt = 0; attempt < 30 && !improved; attempt++) {
      const step = dampedStep(jac, residuals, lambda);
      const candidate = step ? residualsAt(A + step[0], b + step[1]) : null;
      const candidateSse = candidate ? sumSquares(candidate) : Number.NaN;
      if (step && candidate && candidateSse <= sse) {
        const relative = (sse - candidateSse) / Math.max(sse, 1e-300);
        const size = Math.max(
          Math.abs(step[0]) / (Math.abs(A) + 1e-12),
          Math.abs(step[1]) / (Math.abs(b) + 1e-12)
        );
        A += step[0];
        b += step[1];
        residuals = candidate;
        sse = candidateSse;
        lambda = Math.max(lambda / 10, 1e-15);
        improved = true;
        converged = relative < 1e-15 || size < 1e-13;
      } else {
        lambda *= 10;
      }
    }
    // No damping level improves the fit: the minimum is reached to machine precision.
    converged = converged || !improved;
  }
  const jac = model.t.map((t) => model.jacobian(t, A, b));
  const qr = qrLeastSquares(
    [jac.map((row) => row[0]), jac.map((row) => row[1])],
    new Array<number>(ys.length).fill(0)
  );
  if (qr.rank < 2) {
    return null;
  }
  return { A, b, sse, converged, covariance: qr.covariance };
}

function fitExpPower(
  id: "exponential" | "power",
  xs: readonly number[],
  ys: readonly number[],
  method: FitMethod
): CurveFit | null {
  const n = xs.length;
  const model = scaledModel(id, xs);
  // The straight-line fit on the log scale: ln y = ln A + b·g(t).
  const logY = ys.map(Math.log);
  const regressor = id === "exponential" ? model.t : model.t.map(Math.log);
  const line = qrLeastSquares([new Array<number>(n).fill(1), regressor], logY);
  if (line.rank < 2) {
    return null;
  }
  const lnA = line.coefficients[0];
  const b0 = line.coefficients[1];
  const sstLog = sumSquares(logY.map((v) => v - mean(logY)));
  const transformedR2 = sstLog > 0 ? 1 - line.sse / sstLog : Number.NaN;
  let A = Math.exp(lnA);
  let b = b0;
  let covariance = line.covariance;
  let sigma2 = line.sse / (n - 2);
  let converged = true;
  let useTransformCovariance = true;
  if (method === "original") {
    const lm = levenbergMarquardt(model, ys, [A, b]);
    if (lm === null) {
      return null;
    }
    ({ A, b, covariance, converged } = lm);
    sigma2 = lm.sse / (n - 2);
    useTransformCovariance = false;
  }
  // Delta method to the reported (a, b): a = toA(A, b).
  const [dAdA, dAdb] = model.dA(A, b);
  const cov = useTransformCovariance
    ? [
        // ln A and b: Var(A) = A² Var(ln A), Cov(A, b) = A Cov(ln A, b).
        [A * A * covariance[0][0], A * covariance[0][1]],
        [A * covariance[0][1], covariance[1][1]],
      ]
    : covariance;
  const varA =
    dAdA * dAdA * cov[0][0] +
    2 * dAdA * dAdb * cov[0][1] +
    dAdb * dAdb * cov[1][1];
  const df = n - 2;
  const a = model.toA(A, b);
  const parameters = [
    parameter("a", a, Math.sqrt(Math.max(0, sigma2 * varA)), df),
    parameter("b", b, Math.sqrt(Math.max(0, sigma2 * cov[1][1])), df),
  ];
  const predict = (x: number) => model.value(model.toT(x), A, b);
  return finish(id, xs, ys, predict, parameters, {
    // Only the transformed method is a fit on the log scale, so only it has a log R².
    transformedR2: method === "transformed" ? transformedR2 : null,
    converged,
  });
}

// ---------------------------------------------------------------------------
// Public entry points
// ---------------------------------------------------------------------------

function distinct(values: readonly number[]): number {
  return new Set(values).size;
}

/** Fit one model, or say why it cannot be fitted to these data. */
export function fitModel(
  id: ModelId,
  xs: readonly number[],
  ys: readonly number[],
  method: FitMethod = "original"
): ModelResult {
  const n = xs.length;
  const degree = polynomialDegree(id);
  const parameters = degree === null ? 2 : degree + 1;
  if (n < parameters + 2) {
    return { id, fit: null, reason: "needsMoreData" };
  }
  if (distinct(xs) < parameters) {
    return { id, fit: null, reason: "needsDistinctX" };
  }
  if ((id === "logarithmic" || id === "power") && xs.some((x) => x <= 0)) {
    return { id, fit: null, reason: "needsPositiveX" };
  }
  if ((id === "exponential" || id === "power") && ys.some((y) => y <= 0)) {
    return { id, fit: null, reason: "needsPositiveY" };
  }
  let fit: CurveFit | null;
  if (degree !== null) {
    fit = fitPolynomial(id, degree, xs, ys);
  } else if (id === "logarithmic") {
    fit = fitLogarithmic(xs, ys);
  } else {
    fit = fitExpPower(id as "exponential" | "power", xs, ys, method);
  }
  return fit === null
    ? { id, fit: null, reason: "failed" }
    : { id, fit, reason: null };
}

export function fitModels(
  ids: readonly ModelId[],
  xs: readonly number[],
  ys: readonly number[],
  method: FitMethod = "original"
): ModelResult[] {
  return ids.map((id) => fitModel(id, xs, ys, method));
}
